import { joinURL, withQuery } from 'ufo'
import { getAccomUrl } from '~/utils/accommodations'

export default function useURLs() {
  const runtimeConfig = useRuntimeConfig()
  const params = useParams()
  const partner = usePartner()
  const confData = useConfdata()
  const context = useAppContext()

  function buildUrl(
    path: string,
    query: Params,
    options?: { languagePrefix?: boolean },
  ): URL {
    const url = useRequestURL()
    url.pathname = buildPath(
      options?.languagePrefix ? confData.languagePrefix : '',
      path,
    )
    url.search = new URLSearchParams(asQueryParams(unpack(query))).toString()

    return url
  }

  function buildSearchUrl(
    filters: Params,
    geo?: {
      country?: SearchApiDestFacet
      region?: SearchApiDestFacet
      place?: SearchApiDestFacet
    },
  ): URL {
    const { searchfilterconfs } = confData.additionalBody as SearchPageBody
    const { prefixSearchpage } = confData
    const maxPathLength = filters.place || filters.region ? 3 : 2

    const geoConfs = [
      {
        filter: 'country',
        slug: {
          value: filters.country,
          path: geo?.country?.slug || filters.country,
        },
      },
      {
        filter: 'region',
        slug: {
          value: filters.region,
          path: geo?.region?.slug || filters.region,
        },
      },
      {
        filter: 'place',
        slug: { value: filters.place, path: geo?.place?.slug || filters.place },
      },
    ]

    const slugs = new Map(
      [...geoConfs, ...(searchfilterconfs ?? [])]
        .filter(
          ({ slug, filter }) => filters[filter] && slug.value && slug.path,
        )
        .map(({ slug, filter }) => {
          if (['sea', 'lake', 'ski', 'center'].includes(filter)) {
            return slug.value === filters[filter]
              ? [filter, slug.path]
              : undefined
          }
          if (filter === 'pax') {
            return slug.value &&
              parseInt(slug.value) === tryParseInt(filters[filter]?.toString())
              ? [filter, slug.path]
              : undefined
          }
          return [filter, slug.path]
        })
        .filter(Boolean)
        .slice(0, maxPathLength) as [string, string][],
    )

    const path = slugs.size
      ? buildPath(...slugs.values())
      : buildPath(prefixSearchpage)
    const query = {
      ...Object.fromEntries(
        Object.entries(filters).filter(([key]) => !slugs.has(key)),
      ),
      partnerid: partner.id,
      ...params.persistent,
    }

    return buildUrl(path, query, { languagePrefix: true })
  }

  function buildAccommodationUrl(
    accom: { slug: string; code: string; trip?: Trip },
    trip?: Partial<UnparsedTripParams>,
  ): URL {
    return new URL(getAccomUrl(context, accom, trip), useRequestURL())
  }

  function buildBookingUrl(
    accom: { code: string },
    { checkin, checkout, pax }: TripParams,
    options?: BookingOptions,
  ): URL {
    return buildUrl('booking', {
      accommodation: accom.code,
      checkin: toDate(checkin),
      checkout: toDate(checkout),
      pax: pax.toString(),
      language: confData.languagePrefix ? confData.language : undefined,
      isOption: options?.isOption ? 'true' : undefined,
      partnerid: partner.id,
      ...params.persistent,
    })
  }

  function buildImageUrl(imageId: string, transformation?: string): string {
    return joinURL(
      runtimeConfig.public.imagekitBase,
      imageId,
      transformation ?? '',
    )
  }

  function buildReviewsUrl(): URL {
    // TODO: slugs are not the best base for reviews-link, sometimes with leading slash, sometimes without
    // conf-api should deliver link to review-page
    const { prefixSearchpage, prefixReviewspage, languagePrefix } = confData

    const url = useRequestURL()
    url.pathname = buildPath(
      languagePrefix,
      prefixReviewspage,
      deleteFromPath(url.pathname, prefixSearchpage, languagePrefix),
    )
    url.searchParams.delete('page')

    return url
  }

  function buildValidUrl(href: string): URL {
    const url = new URL(href, useRequestURL())
    url.search = new URLSearchParams({
      ...asQueryParams(params.persistent),
      ...Object.fromEntries(url.searchParams),
    }).toString()
    return url
  }

  function buildRedirectUrl(href: string): URL {
    return new URL(withQuery(href, useRoute().query), useRequestURL())
  }

  function buildBookmarksUrl(): string {
    const { href } = confData.links?.bookmarks ?? {}

    return href ? withQuery(href, { partnerid: partner.id || undefined }) : ''
  }

  function buildNewsletterUrl() {
    // Host does not matter
    const url = new URL('https://www.interhome.com')
    url.pathname = `${confData.languagePrefix}/newsletter`
    url.searchParams.set('type', 'Bar')

    url.search = new URLSearchParams({
      ...(Object.fromEntries(url.searchParams) as Record<string, string>),
      ...(params.persistent as Record<string, string>),
    }).toString()
    return url.pathname + url.search
  }

  return {
    buildUrl,
    buildSearchUrl,
    buildImageUrl,
    buildAccommodationUrl,
    buildBookingUrl,
    buildReviewsUrl,
    buildValidUrl,
    buildRedirectUrl,
    buildBookmarksUrl,
    buildNewsletterUrl,
  }
}
